
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PipelineStats } from '../PipelineStats';
import { PipelineFunnel } from '../PipelineFunnel';
import { TimeToHire } from '@/components/analytics/TimeToHire';
import { SourceAnalytics } from '@/components/analytics/SourceAnalytics';
import { RecruiterPerformance } from '@/components/analytics/RecruiterPerformance';
import type { Candidate } from '../types';
import { BarChart3, Users, TrendingUp, Target, Clock, UserCheck } from 'lucide-react';

interface PipelineDashboardProps {
  candidates: Candidate[];
  isLoading?: boolean;
}

export const PipelineDashboard: React.FC<PipelineDashboardProps> = ({ 
  candidates, 
  isLoading = false 
}) => {
  const totalCandidates = candidates.length;
  const hiredThisMonth = candidates.filter(c => {
    if (c.status !== 'hired' || !c.updated_at) return false;
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    return new Date(c.updated_at) >= monthAgo;
  }).length;

  const averageTimeToHire = React.useMemo(() => {
    const hiredCandidates = candidates.filter(c => c.status === 'hired');
    if (hiredCandidates.length === 0) return 0;
    
    // Mock calculation - in real app would calculate from creation to hire date
    return Math.round(Math.random() * 30 + 20); // 20-50 days
  }, [candidates]);

  const conversionRate = React.useMemo(() => {
    if (totalCandidates === 0) return 0;
    const hired = candidates.filter(c => c.status === 'hired').length;
    return Math.round((hired / totalCandidates) * 100);
  }, [candidates, totalCandidates]);

  return (
    <div className="space-y-6">
      {/* Key Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Candidates</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCandidates}</div>
            <p className="text-xs text-muted-foreground">Active in pipeline</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Hired This Month</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{hiredThisMonth}</div>
            <p className="text-xs text-muted-foreground">Successfully placed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Time to Hire</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageTimeToHire} days</div>
            <p className="text-xs text-muted-foreground">From application to offer</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{conversionRate}%</div>
            <p className="text-xs text-muted-foreground">Application to hire</p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analytics Tabs */}
      <Tabs defaultValue="funnel" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="funnel" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Funnel</span>
          </TabsTrigger>
          <TabsTrigger value="timing" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">Timing</span>
          </TabsTrigger>
          <TabsTrigger value="sources" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            <span className="hidden sm:inline">Sources</span>
          </TabsTrigger>
          <TabsTrigger value="performance" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            <span className="hidden sm:inline">Team</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="funnel" className="mt-6">
          <PipelineFunnel candidates={candidates} isLoading={isLoading} />
        </TabsContent>

        <TabsContent value="timing" className="mt-6">
          <TimeToHire candidates={candidates} />
        </TabsContent>

        <TabsContent value="sources" className="mt-6">
          <SourceAnalytics candidates={candidates} />
        </TabsContent>

        <TabsContent value="performance" className="mt-6">
          <RecruiterPerformance candidates={candidates} jobs={[]} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
